import { z } from 'zod';

/** Same cap as the order line's preflight URL column. */
export const SCRIPT_REFERENCE_VIDEO_URL_MAX = 2048;

/**
 * `new URL()` also accepts `https:youtu.be/x` and `https:/youtu.be/x`, which an
 * `href` resolves against our own host. Only the explicit `//` form is a link.
 */
const EXPLICIT_HTTP_PREFIX = /^https?:\/\//i;

export const SCRIPT_REFERENCE_URL_TOO_LONG_MESSAGE = 'Links can be at most 2,048 characters';

/** A pasted link the partner can open: explicit http(s)://, with a dotted host. */
export function isFullHttpUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!EXPLICIT_HTTP_PREFIX.test(trimmed)) return false;
  try {
    return new URL(trimmed).hostname.includes('.');
  } catch {
    return false;
  }
}

export const ScriptReferenceVideoUrlSchema = z
  .string()
  .trim()
  .min(1, { message: 'Add the reference URL' })
  .max(SCRIPT_REFERENCE_VIDEO_URL_MAX, { message: SCRIPT_REFERENCE_URL_TOO_LONG_MESSAGE })
  .refine(isFullHttpUrl, { message: 'Enter a full link that starts with https://' });

/** One reference video for one item (script or brief) on a send. */
export const OrderScriptReferenceSchema = z.object({
  /** The script id or brief id the link belongs to. */
  itemId: z.string().min(1),
  url: ScriptReferenceVideoUrlSchema,
});
export type OrderScriptReference = z.infer<typeof OrderScriptReferenceSchema>;

export type ReferenceVideoPlatform =
  | 'tiktok'
  | 'youtube'
  | 'instagram'
  | 'google-drive'
  | 'vimeo'
  | 'other';

export interface ReferenceVideoSource {
  platform: ReferenceVideoPlatform;
  /** Platform name, or the bare host for an unknown site. */
  label: string;
  /** Account handle when the path carries one, e.g. tiktok.com/@name/... */
  handle: string | null;
}

const PLATFORM_HOSTS: ReadonlyArray<{
  platform: Exclude<ReferenceVideoPlatform, 'other'>;
  label: string;
  hosts: readonly string[];
}> = [
  { platform: 'tiktok', label: 'TikTok', hosts: ['tiktok.com'] },
  { platform: 'youtube', label: 'YouTube', hosts: ['youtube.com', 'youtu.be'] },
  { platform: 'instagram', label: 'Instagram', hosts: ['instagram.com'] },
  { platform: 'google-drive', label: 'Google Drive', hosts: ['drive.google.com'] },
  { platform: 'vimeo', label: 'Vimeo', hosts: ['vimeo.com'] },
];

const HANDLE_SEGMENT = /^@[\w.-]+$/;

function matchesHost(hostname: string, host: string): boolean {
  return hostname === host || hostname.endsWith(`.${host}`);
}

/**
 * Names the platform behind a reference link from its address alone, so the
 * dialog and the email can label it without fetching anything.
 */
export function describeReferenceVideoUrl(value: string): ReferenceVideoSource | null {
  if (!isFullHttpUrl(value)) return null;
  const url = new URL(value.trim());
  const hostname = url.hostname.toLowerCase().replace(/^www\./, '');
  const firstSegment = url.pathname.split('/').find((segment) => segment.length > 0) ?? '';
  const handle = HANDLE_SEGMENT.test(firstSegment) ? firstSegment : null;
  const known = PLATFORM_HOSTS.find((entry) =>
    entry.hosts.some((host) => matchesHost(hostname, host)),
  );
  if (known) return { platform: known.platform, label: known.label, handle };
  return { platform: 'other', label: hostname, handle: null };
}
